import React, { useEffect, useRef } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppContextProvider from "./context/Appcontext";
import "./App.scss";
import { useSessionStorage } from "./hooks/general/useStorage";
import { STORAGE_LANG_KEY } from "./TranslationConstants";
import { getSearchParam } from "./hooks/general/useSearchParam";
import { HeaderNavigation } from "./components/layout/header/HeaderNavigation";
import { Checkout } from "./components/Checkout";
import { FooterWrapper } from "./components/layout/footer/FooterWrapper";
import {
  CookieBanner,
  CookieConsentContextProvider,
  CookieConsentReactProps,
} from "hds-react-next";
import { getCookieConsentSiteSettings } from "./components/cookieConsent/commonSiteSettings";

export default function App() {
  const { i18n } = useTranslation();
  const cookieRef = useRef(null);

  /**
   * This code checks for use of a language code in the url that is not the
   * current one in storage or url. If found, it updates the storage value for language code.
   */
  const [langCode, updateLang] = useSessionStorage(STORAGE_LANG_KEY);
  const currentLangCode = getSearchParam("lang");
  const previousLangCode = useRef("");

  // Ensure that we use the correct language code.
  useEffect(() => {
    if (typeof langCode === "string" && previousLangCode.current !== langCode) {
      if (!currentLangCode) {
        // Set the correct language
        i18n.changeLanguage(langCode);
      } else if (i18n.language !== langCode) {
        // Goes here when language code was changed in the url and syncs it with stored value.
        updateLang(i18n.language);
        i18n.changeLanguage(currentLangCode);
      }

      // And update the ref.
      previousLangCode.current = langCode;
      // KYV-1329 set the html lang tag to current langcode
      document.documentElement.lang = langCode;
    }
  }, [langCode, previousLangCode, currentLangCode, i18n, updateLang]);

  const onChange: CookieConsentReactProps["onChange"] = (event) => {
    console.log("consent event", event);
  };

  return (
    <AppContextProvider>
      <CookieConsentContextProvider
        onChange={onChange}
        options={{ language: langCode || "fi" }}
        siteSettings={{
          ...getCookieConsentSiteSettings(window.location.hostname),
        }}
      >
        <div id={"cookie-banner"} tabIndex={-1}>
          <CookieBanner />
        </div>
        <Router>
          <div className="App">
            <header>
              <HeaderNavigation />
            </header>
            <main id="main-content" tabIndex={-1}>
              <Checkout />
            </main>
            <footer>
              <FooterWrapper />
            </footer>
          </div>
        </Router>
      </CookieConsentContextProvider>
    </AppContextProvider>
  );
}
